import { gql, useQuery } from '@apollo/client';
import { LEARNING_ACTIVITY_PDP_PARAMS, isLocalizedCourseCatalogItemEnabled } from '../../common/constants';
import { ApplicationUser } from '../auth/useAuth';
import { shouldQueryRegistrationCounter } from '../../common/actionButtonRules/registrationCounterQueryResolver';
import { FeatureFlagsCondition } from '../../common/actionButtonRules/conditions';
import { useLogger } from '@amzn/xb-hooks';
import { LEARNING_ACTIVITY_ERROR, LEARNING_ACTIVITYWITHOUTRREGISTRATIONCOUNTER_ERROR } from '../../common/rumMetrics';

export const LEARNING_ACTIVITY = gql`
    query learningActivity($pk: ID! $queryRegistrationCounter: Boolean! $queryCourseCatalogItemLimited: Boolean! $queryLocalizedCourseCatalogItem: Boolean!) {
        learningActivity(pk: $pk) {
          ${LEARNING_ACTIVITY_PDP_PARAMS}
        }
    }
`;

const useLearningActivityWithoutRegistrationCounter = (
    pk: string,
    queryCourseCatalogItemLimited: boolean,
    queryLocalizedCourseCatalogItem: boolean,
) => {
    const { log, count } = useLogger();
    return useQuery(LEARNING_ACTIVITY, {
        skip: !pk,
        variables: {
            pk,
            queryRegistrationCounter: false,
            queryCourseCatalogItemLimited,
            queryLocalizedCourseCatalogItem,
        },
        notifyOnNetworkStatusChange: true,
        onError: (error) => {
            log.error('Error requesting learning activity', error);
            count(LEARNING_ACTIVITYWITHOUTRREGISTRATIONCOUNTER_ERROR);
        },
    });
};

export const useLearningActivity = (pk: string, user: ApplicationUser | undefined, features: FeatureFlagsCondition, orgId?: string) => {
    const isB2IUser = user?.organizationType === 'CUSTOMERS_B2I' || user?.organizationType === 'PARTNERS_B2I';
    const queryCourseCatalogItemLimited = isB2IUser && features.adcLinkSharing && !!orgId;
    const queryLocalizedCourseCatalogItem = isLocalizedCourseCatalogItemEnabled();

    const firstQueryResult = useLearningActivityWithoutRegistrationCounter(
        pk,
        queryCourseCatalogItemLimited,
        queryLocalizedCourseCatalogItem,
    );
    const { log, count } = useLogger();
    const shouldSkipSecondQuery = !pk || !user?.organizationId;

    const secondQueryResult = useQuery(LEARNING_ACTIVITY, {
        skip: shouldSkipSecondQuery,
        variables: {
            pk,
            queryRegistrationCounter: shouldQueryRegistrationCounter(firstQueryResult.data?.learningActivity, user, features),
            queryCourseCatalogItemLimited,
            queryLocalizedCourseCatalogItem,
        },
        notifyOnNetworkStatusChange: true,
        onError: (error) => {
            log.error('Error requesting learning activity', error);
            count(LEARNING_ACTIVITY_ERROR);
        },
    });

    // If second query is skipped, return the first query's result
    if (shouldSkipSecondQuery) {
        return firstQueryResult;
    }

    return secondQueryResult;
};
